import {
  Button,
  ComposedModal,
  InlineNotification,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from '@carbon/react';
import { useAtom } from 'jotai';
import { useState } from 'react';

import { SuccessToast } from 'components/success-toast/success-toast';

import { bulkUnconfirmLoadableAtom, unconfirmTargetRowAtom } from './atoms';
import { certifiedStreamsForRow, getMutationErrorMessage } from './utils';
import { useWorkspaceConfirmation } from './workspace-confirmation-context';
import styles from './unconfirm-modal.module.scss';

/**
 * Confirms the user wants to clear an existing slot confirmation and fires the
 * BE DELETE. The existing `invalidateAfterConfirmationEvent` helper handles the
 * cascade (slot list + workspace refetch) so the row's Status flips to
 * "Not Started" and the Tasks counter + summary tiles update on their own.
 *
 * @return {JSX.Element} The rendered modal (hidden when no row is targeted).
 */
export const UnconfirmModal = (): JSX.Element => {
  const [row, setRow] = useAtom(unconfirmTargetRowAtom);
  const { workspaceId } = useWorkspaceConfirmation();
  const [loadable, bulkUnconfirm] = useAtom(bulkUnconfirmLoadableAtom);
  const [error, setError] = useState<string | null>(null);
  // Decoupled from the modal session so the toast survives the modal closing.
  const [successToastVisible, setSuccessToastVisible] = useState(false);

  const loading = loadable.state === 'loading';

  const handleClose = (): void => {
    if (loading) return;
    setRow(null);
    setError(null);
  };

  const handleUnconfirm = async (): Promise<void> => {
    if (loading || !row) return;
    setError(null);
    try {
      // Uncertify ALL of the row's certified streams in one bulk request so the
      // BE stamps a shared `unconfirmed_at` and the activity feed collapses them
      // into a single "Unconfirmed Interest" card (N single-stream DELETEs via
      // `unconfirmSlotAtom` would stay separate). `unconfirmSlotAtom` is kept for
      // the genuine single-stream case.
      await bulkUnconfirm(workspaceId, [
        { slot_id: row.slotId, streams: certifiedStreamsForRow(row) },
      ]);
      setRow(null);
      setSuccessToastVisible(true);
    } catch (err) {
      setError(getMutationErrorMessage(err, "Couldn't unconfirm slot."));
    }
  };

  return (
    <>
      <ComposedModal
        open={row !== null}
        onClose={handleClose}
        preventCloseOnClickOutside
        size="sm"
        danger
      >
        <ModalHeader title="Remove certification" />
        <ModalBody>
          {row && (
            <p className={styles.body}>
              Are you sure you want to remove the certified interest for slot{' '}
              <strong>{row.slotId}</strong>? This will clear the values you
              entered or selected and return the slot to an uncertified state.
            </p>
          )}
          {error && (
            <InlineNotification
              className={styles.notification}
              kind="error"
              lowContrast
              hideCloseButton
              title={error}
            />
          )}
        </ModalBody>
        <ModalFooter>
          <Button kind="secondary" onClick={handleClose} disabled={loading}>
            Cancel
          </Button>
          <Button kind="danger" disabled={loading} onClick={handleUnconfirm}>
            {loading ? 'Removing…' : 'Remove'}
          </Button>
        </ModalFooter>
      </ComposedModal>
      {successToastVisible && (
        <SuccessToast
          message="Slot unconfirmed"
          onClose={() => {
            setSuccessToastVisible(false);
            return false;
          }}
        />
      )}
    </>
  );
};
